import { useEffect } from "react";
import { useTrustNGOSession } from "@/hooks/useTrustNGOSession";
import { getAuthBaseUrl, loginWithTrustNGO, logout } from "@/lib/trustngo-auth";
import { Button } from "@/components/ui/button";
import { ShieldX } from "lucide-react";

interface WorkspaceRouteProps {
  children: React.ReactNode;
}

const SsoRedirect = () => {
  useEffect(() => {
    loginWithTrustNGO();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground">Redirecting to TrustNGO login...</p>
    </div>
  );
};

export default function WorkspaceRoute({ children }: WorkspaceRouteProps) {
  const { user, session, loading } = useTrustNGOSession();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!user || !session) {
    return <SsoRedirect />;
  }

  if (!session.tenants?.includes("sigmacert")) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
        <ShieldX className="h-16 w-16 text-destructive" />
        <h1 className="text-2xl font-bold">Workspace Access Denied</h1>
        <p className="text-muted-foreground max-w-md">
          Your account does not have access to the SigmaCert workspace. Please request access from your organization administrator.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => logout()}>Back to TrustNGO</Button>
          <Button onClick={() => window.open(getAuthBaseUrl(), "_blank")}>
            Request Access
          </Button>
        </div>
      </div>
    );
  }

  // If we get here, user is authenticated and has sigmacert access
  return <>{children}</>;
}
